/**
 * Secure Logger Utility
 *
 * This utility provides secure logging that:
 * - Only logs detailed information in development
 * - Sanitizes sensitive data in production
 * - Provides different log levels
 * - Can be easily disabled for production builds
 */

interface LogConfig {
  isDevelopment: boolean;
  enableConsoleLogging: boolean;
  logLevel: "error" | "warn" | "info" | "debug";
}

class SecureLogger {
  private config: LogConfig;

  constructor() {
    const isProduction = import.meta.env.PROD || import.meta.env.MODE === "production";
    
    this.config = {
      isDevelopment: import.meta.env.DEV || import.meta.env.MODE === "development",
      // In production, ALWAYS disable console logging unless explicitly enabled
      enableConsoleLogging: isProduction 
        ? false 
        : (import.meta.env.VITE_ENABLE_DEBUG_LOGGING === "true" || import.meta.env.VITE_VERBOSE_LOGGING === "true"),
      logLevel: (() => {
        const level = import.meta.env.VITE_LOG_LEVEL as string | undefined;
        return level === "error" || level === "warn" || level === "info" || level === "debug"
          ? level
          : (import.meta.env.DEV ? "debug" : "error");
      })(),
    };

    // Additional safety check: In production builds, force disable console logging
    if (isProduction || import.meta.env.VITE_DISABLE_CONSOLE_LOGS === "true") {
      this.config.enableConsoleLogging = false;
    }
  }

  /**
   * Sanitize sensitive data from objects
   */
  private sanitizeData(data: unknown): unknown {
    if (data === null || typeof data !== "object") {
      return data;
    }

    const sensitiveKeys = [
      "uid",
      "userId",
      "user_id",
      "email",
      "password",
      "token",
      "key",
      "secret",
      "auth",
      "credential",
      "phone",
      "address",
      "firstName",
      "lastName",
    ];

  const sanitized: Record<string, unknown> = { ...(data as Record<string, unknown>) };

    for (const key in sanitized) {
      if (sensitiveKeys.some((sensitive) => key.toLowerCase().includes(sensitive))) {
        if (this.config.isDevelopment) {
          // In development, show partial data
          const val = sanitized[key];
          if (typeof val === "string" && val.length > 4) {
            sanitized[key] = val.substring(0, 4) + "***";
          } else {
            sanitized[key] = "***";
          }
        } else {
          // In production, completely hide sensitive data
          sanitized[key] = "[REDACTED]";
        }
      }
    }

    return sanitized;
  }

  /**
   * Safe logging for development debugging - only when explicitly enabled
   */
  debug(message: string, data?: unknown): void {
    if (!this.config.enableConsoleLogging || this.config.logLevel !== "debug") {
      return;
    }

    if (data) {
      console.log(`[DEBUG] ${message}`, this.sanitizeData(data));
    } else {
      console.log(`[DEBUG] ${message}`);
    }
  }

  /**
   * Information logging - minimal by default
   */
  info(message: string, data?: unknown): void {
    if (!this.config.enableConsoleLogging) {
      return;
    }

    // Only log info if explicitly enabled or in debug mode
    if (this.config.logLevel === "info" || this.config.logLevel === "debug") {
      if (data && this.config.isDevelopment) {
        console.log(`[INFO] ${message}`, this.sanitizeData(data));
      } else {
        console.log(`[INFO] ${message}`);
      }
    }
  }

  /**
   * Warning logging
   */
  warn(message: string, data?: unknown): void {
    if (!this.config.enableConsoleLogging) {
      return;
    }

    if (data && this.config.isDevelopment) {
      console.warn(`[WARN] ${message}`, this.sanitizeData(data));
    } else {
      console.warn(`[WARN] ${message}`);
    }
  }

  /**
   * Error logging - always enabled but sanitized
   */
  error(message: string, error?: unknown): void {
    if (this.config.isDevelopment) {
      if (error) {
        console.error(`[ERROR] ${message}`, error);
      } else {
        console.error(`[ERROR] ${message}`);
      }
    } else {
      // In production, log generic error without stack traces or sensitive info
      console.error(`[ERROR] ${message}`);

      // You could also send errors to a monitoring service here
      // this.sendToMonitoringService(message, error);
    }
  }

  /**
   * Success logging - only when explicitly enabled
   */
  success(message: string, data?: unknown): void {
    if (!this.config.enableConsoleLogging) {
      return;
    }

    // Only log success if explicitly enabled
    if (this.config.logLevel === "info" || this.config.logLevel === "debug") {
      if (data && this.config.isDevelopment) {
        console.log(`[SUCCESS] ${message}`, this.sanitizeData(data));
      } else {
        console.log(`[SUCCESS] ${message}`);
      }
    }
  }

  /**
   * Process timing for performance monitoring - only in development
   */
  time(label: string): void {
    if (this.config.isDevelopment && this.config.enableConsoleLogging) {
      console.time(`[TIMER] ${label}`);
    }
  }

  timeEnd(label: string): void {
    if (this.config.isDevelopment && this.config.enableConsoleLogging) {
      console.timeEnd(`[TIMER] ${label}`);
    }
  }

  /**
   * Security-sensitive operation logging
   */
  security(message: string, operation: string): void {
    if (this.config.isDevelopment && this.config.enableConsoleLogging) {
      console.log(`[SECURITY] ${operation}: ${message}`);
    } else {
      // In production, only log critical security events
      if (this.config.logLevel === "info" || this.config.logLevel === "debug") {
        console.log(`[SECURITY] Operation completed: ${operation}`);
      }
    }
  }
}

// Lazy singleton instance to avoid eager initialization during module evaluation
let _loggerInstance: SecureLogger | null = null;

export function getLogger(): SecureLogger {
  if (!_loggerInstance) {
    _loggerInstance = new SecureLogger();
  }
  return _loggerInstance;
}

// Backward-compatible export: proxy that defers to the lazy instance
export const logger = new Proxy({} as SecureLogger, {
  get(_target, prop, receiver) {
    return Reflect.get(getLogger() as unknown as object, prop, receiver);
  },
}) as SecureLogger;

// Convenience functions
export const logDebug = (message: string, data?: unknown) => getLogger().debug(message, data);
export const logInfo = (message: string, data?: unknown) => getLogger().info(message, data);
export const logWarn = (message: string, data?: unknown) => getLogger().warn(message, data);
export const logError = (message: string, error?: unknown) => getLogger().error(message, error);
export const logSuccess = (message: string, data?: unknown) => getLogger().success(message, data);
export const logSecurity = (message: string, operation: string) =>
  getLogger().security(message, operation);
